/**
 * Tokens do sistema visual "O Catálogo Noturno".
 *
 * Nenhum valor aqui é novo — são os mesmos já documentados em DESIGN.md,
 * extraídos do código em produção. Este arquivo não é consumido por
 * nenhum componente ainda (ver PLANO_REFATORACAO_DETALHADO.md, A3.3):
 * ele existe para que a próxima tela que migrar (A3 -> Login, depois A5)
 * tenha um lugar único para puxar cor, espaço, raio, sombra, tipografia
 * e movimento, em vez de declarar o valor de novo.
 */
export const tokens = {
  color: {
    // Neutros — não variam por cliente, são a base do sistema.
    stageBlack: '#0f0f0f', // fundo — Preto de Palco
    cardCharcoal: '#222222', // faixa de metadados do card de curso
    railGraphite: '#2c2f39', // trilho vazio da barra de progresso
    // Escurecido de #7a8095 (~20%) em relacao ao valor original documentado:
    // branco sobre #7a8095 mede ~4:1, abaixo do minimo AA de 4.5:1 mesmo
    // sendo a MELHOR cor de texto possivel (ambar sobre #7a8095 da ~2:1,
    // pior ainda). Contraste de branco sobre este tom: ~5.9:1.
    fieldSlate: '#626677', // preenchimento de inputs e selects
    textWhite: '#ffffff',
    // Icone secundario DENTRO do campo (mostrar/ocultar senha). Clareado de
    // #b4bad4, que media 2.96:1 sobre fieldSlate e falhava o minimo de 3:1
    // para elemento nao textual. Este tom da 3.46:1 e continua recuado em
    // relacao ao icone semantico da esquerda, que e branco.
    iconGray: '#c4c9dd',
    textMist: '#e9eaed', // texto de navegação
    textAsh: '#aaaaaa', // metadado secundário do card
    inkCharcoal: '#353536', // texto SOBRE o ouro — nunca sobre o palco

    // Acento — Ouro de Marquise. Único por papel: preço, progresso,
    // aba ativa, ação primária. Ver "Regra da Voz Única" em DESIGN.md.
    marqueeGold: '#fed44a',
    focusAmber: '#ffa22b', // exclusivo do estado de campo (foco/preenchido)

    // Semântico
    errorRed: '#c53030',

    // Vestigial — nomeado "primary" no CSS legado mas com só 8 usos
    // contra 28 do ouro. Não construir nada novo sobre ele.
    legacyCyan: '#49baae',
  },

  space: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    gutter: '5%', // margem lateral de header e contêineres de curso
  },

  radius: {
    xs: '4px',
    sm: '6px',
    md: '12px', // barra de progresso, carrossel
    pill: '30px', // exclusivo de campo/select
    circle: '50%', // avatar, badge
  },

  shadow: {
    innerVignette: 'inset 0 0 20px 10px rgba(0, 0, 0, 0.6)', // borda de banner/imagem grande
    imageSeat: '0 0 10px rgba(0, 0, 0, 0.4)', // halo sob thumbnail
    hoverHalo: 'rgb(0 0 0 / 90%) 0px 0px 8px', // exclusivo do card de curso em hover
  },

  font: {
    title: { family: "Inter, sans-serif", size: '2rem', weight: 600 },
    heading: { family: "'Roboto Slab', sans-serif", size: '16px', weight: 800 },
    body: { family: "Inter, sans-serif", size: '14px', weight: 400 },
    label: { family: "Inter, sans-serif", size: '14px', weight: 500 },
    caption: { family: "Inter, sans-serif", size: '12px', weight: 400 },
  },

  motion: {
    quick: '0.2s', // resposta imediata: filter opacity, escala do card
    slow: '0.4s', // cor de link, borda de campo, preenchimento de progresso
    reveal: '0.5s', // entrada da fileira ao rolar a página
    // Atraso de ENTRADA do hover do card. Uma varredura rápida do mouse
    // atravessa a fileira sem acender nada; a SAÍDA continua sem atraso.
    hoverIn: '0.28s',
    easeOut: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  },

  // Os quatro primeiros são os documentados em DESIGN.md ("Breakpoints").
  // xl e xxl são novos: existem só para a fileira ganhar coluna em tela
  // larga, onde o card de 260px fixo deixava sobra irregular.
  //
  // Usar SEMPRE pelo helper `media` abaixo. Escrever a media query à mão é
  // como nasceu a zona morta de 980px: o scroll abria em `max-width: 979px`,
  // as setas sumiam em `max-width: 980px` e o swap de card escolhia o desktop
  // em `>= 980` — nessa largura exata não havia scroll nem setas.
  breakpoint: {
    xs: 425, // avatar e divisória do header somem
    sm: 768, // ponto de virada principal (70 ocorrências no código)
    md: 980, // proporção da capa vira paisagem; player vira overlay
    lg: 1270, // navegação colapsa em hambúrguer
    xl: 1600,
    xxl: 2160,
  },

  // Altura do header, já documentada em DESIGN.md ("Header de 88px").
  // Existe como token porque a tela de aula precisa descontá-la da viewport
  // para caber sem scroll — antes isso era um `height: 88vh` chutado.
  layout: {
    headerHeight: '88px',
  },

  zIndex: {
    row: 1, // fileira em repouso
    rowHover: 2, // fileira com card crescido — abaixo do header
    rowArrow: 4, // seta acima dos cards da própria fileira
    header: 99, // já praticado por HeaderV2
    modal: 999, // já praticado por Atoms/Modal
  },

  // Métricas da fileira de categoria.
  //
  // INVARIANTE, para o card crescido não ser recortado nem invadir a fileira
  // vizinha — `overflow-x: auto` recorta no PADDING BOX, então o padding
  // vertical do trilho é a área de pintura disponível:
  //
  //   bleedTop    >= (altura do card * (escala - 1) / 2) + hoverLift + blur
  //   bleedBottom >= (altura do card * (escala - 1) / 2) - hoverLift + blur
  //   gap         >= bleedTop
  //
  // Com hoverScale 1.10 num card de ~130px: 6.5 + 8 + 8 = 22.5px < 32px. OK.
  // Se alguém subir hoverScale para os 1.25 documentados, a conta dá ~41px e
  // bleedTop E gap precisam subir juntos.
  row: {
    gapFirst: '56px', // margem acima da primeira fileira
    gap: '40px', // margem acima das demais
    headerGap: '24px', // título da categoria -> trilho
    bleedTop: '32px',
    bleedBottom: '20px',
  },

  card: {
    gap: '8px', // intervalo entre cards da fileira
    hoverScale: '1.10',
    hoverLift: '8px',
    minWidth: '132px', // piso do clamp: telefone estreito
    maxWidth: '320px', // teto do clamp: ultrawide
  },
} as const;

/**
 * Helper de media query. `upTo(md)` e `from(md)` são complementares por
 * construção (979 / 980), o que torna impossível repetir a zona morta.
 *
 *   ${media.from(tokens.breakpoint.md)} { ... }
 */
export const media = {
  upTo: (bp: number) => `@media (max-width: ${bp - 1}px)`,
  from: (bp: number) => `@media (min-width: ${bp}px)`,
};

export type Tokens = typeof tokens;
